import { FormattedMessage } from 'react-intl'
import React, { ReactNode, useEffect, useLayoutEffect, useState } from 'react'
import {
    matchRoutes,
    useLocation,
    Link as RouterLink,
    LinkProps as RouterLinkProps,
} from 'react-router-dom'
import { Color } from './constants'
import pages from '../pages'
import { PositionProps } from '../templates/Position/utils'

// @ts-ignore
export const FormattedMessageWithClass = (props: typeof FormattedMessage.prototype) => (
    <FormattedMessage {...props}>
        {(txt) => <span className={props.className}>{txt}</span>}
    </FormattedMessage>
)

interface ScrollToTopProps {
  callback?: () => void
}
export const ScrollToTop = ({ callback }: ScrollToTopProps) => {
    const { pathname } = useLocation()
    useLayoutEffect(() => {
        window.scrollTo(0, 0)
    callback?.()
    }, [pathname])

    return null
}

interface LinkProps extends RouterLinkProps {
  children: ReactNode
  external?: boolean
}

export const Link = ({ children, onClick, external = false, ...props }: LinkProps) => {
    if (external) {
        return (
            <a
                href={props.to.toString()}
                target='_blank'
                rel='noopener noreferrer nofollow'
                onClick={onClick}
                className={props.className}
            >
                {children}
            </a>
        )
    } else {
        return (
            <RouterLink
                {...props}
                onClick={(e) => {
                    window.scrollTo(0, 0)
          onClick?.(e)
                }}
            >
                {children}
            </RouterLink>
        )
    }
}

export function debounce<Params extends any[]>(
    func: (...args: Params) => void,
    timeout: number,
): (...args: Params) => void {
    let timer: NodeJS.Timeout
    return (...args: Params) => {
        clearTimeout(timer)
        timer = setTimeout(() => {
            func(...args)
        }, timeout)
    }
}

export const rgbtohex = (rgb: string): Color => {
    const hex =
    '#' +
    rgb
        .match(/\d+/g)
        ?.map((val) => {
            const a = Number(val).toString(16)
            return a.length === 1 ? '0' + a : a
        })
        .join('')
    return Color[
    Object.keys(Color)[Object.values(Color).indexOf(hex as unknown as Color)] as keyof typeof Color
    ]
}

export const capitalize = (word: string) => {
    return word.charAt(0).toUpperCase() + word.slice(1)
}

export const mapPosition = (position: any): PositionProps => {
    const descriptions = position.jobDescriptions?.jobDescription
    const base = {
        id: position.id._text,
        title: position.name._text,
        department: position.department._text,
        officeLocation: position.office._text,
        employmentType: position.employmentType._text,
        schedule: position.schedule._text,
    }

    if (!Array.isArray(descriptions)) {
        return base
    }

    return {
        ...base,
        introduction: position.jobDescriptions?.jobDescription?.find(
            (field: { name: { _text: string } }) => field.name._text === 'Introduction',
        )?.value._cdata,
        responsibilities: position.jobDescriptions?.jobDescription?.find(
            (field: { name: { _text: string } }) => field.name._text === 'Responsibilities',
        )?.value._cdata,
        prefQualifications: position.jobDescriptions?.jobDescription?.find(
            (field: { name: { _text: string } }) => field.name._text === 'Preferred qualifications',
        )?.value._cdata,
        minQualifications: position.jobDescriptions?.jobDescription?.find(
            (field: { name: { _text: string } }) => field.name._text === 'Minimum qualifications',
        )?.value._cdata,
        offer: position.jobDescriptions?.jobDescription?.find(
            (field: { name: { _text: string } }) => field.name._text === 'Our offer',
        )?.value._cdata,
    }
}

export const useCurrentPathColor = () => {
    const location = useLocation()
    const routes = matchRoutes(pages, location)
    if (routes) {
        return routes[0].route.color ?? Color.PURPLE
    }

    return Color.PURPLE
}

export const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

    useEffect(() => {
        const calculateIsMobile = () => {
            setIsMobile(window.innerWidth < 768)
        }
        window.addEventListener('resize', calculateIsMobile)
        return () => {
            window.removeEventListener('resize', calculateIsMobile)
        }
    }, [])
    return isMobile
}

export const useIsSafari = () => {
    const [isSafari, setIsSafari] = useState(false)

    useEffect(() => {
        const userAgentString = navigator.userAgent
        let safariAgent = userAgentString.indexOf('Safari') > -1
        const chromeAgent = userAgentString.indexOf('Chrome') > -1
        if (chromeAgent && safariAgent) safariAgent = false
        setIsSafari(safariAgent)
    }, [])
    return isSafari
}

export const useIsFirefox = () => {
    const [isFirefox, setIsFirefox] = useState(false)

    useEffect(() => {
        const userAgentString = navigator.userAgent
        setIsFirefox(userAgentString.indexOf('Firefox') > -1)
    }, [])
    return isFirefox
}

export const slug = (value: string) => {
    return value.toLowerCase().replaceAll(' ', '-')
}

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export const getMonth = (month: number) => months[month]
